import { type NavigateFunction, redirect } from "react-router-dom";

import { standaloneProductsPaths } from "modules/standaloneProducts/standaloneProductsPaths";
import { queryClient } from "pages/Root";

import { ANONYMOUS_STUBBED_PATHS } from "./anonymousApiStubs";
import { onAnonymousBrowsingChange } from "./anonymousBrowsingState";

import { toTemplateMatcher } from "api/queryKeys";

const POST_LOGIN_RETURN_TO_KEY = "anonymousBrowsing.postLoginReturnTo";

/** Route prefixes a logged-out visitor may browse when the platform allows it. */
export const ANONYMOUS_BROWSABLE_PREFIXES = [
  "/home",
  "/discover",
  "/shop",
  "/products/list",
  "/pdp",
  "/search",
  "/search-services",
  "/faqs",
  standaloneProductsPaths.product,
];

/**
 * Evaluated before the allow-list. These pages render prices unguarded or can
 * post a purchase for a guest (`GiftCardsShopContainer` hardcodes
 * `isAuthenticated`), so an anonymous visitor is sent to sign-in instead.
 */
const ANONYMOUS_DENIED_PREFIXES = ["/shop/gift-cards"];

/**
 * Matched exactly, never as a prefix, so each entry lets exactly one page
 * through:
 *
 * - `/cart` shows the local pending cart.
 * - `/cart/checkout` guides visitors through phone verification and any missing
 *   account details, then revalidates into authenticated checkout.
 *
 * Its children — `/cart/checkout/element`, `/cart/checkout/success`,
 * `/cart/checkout/beautifi-return`, `/cart/checkout/membership-rewards`,
 * `/cart/checkout/reward` — stay account-only, which the exact matching gives
 * for free.
 *
 * The products-only storefront mounts the same cart and checkout under its own prefix, and its
 * home is exact for the same reason: the account-only pages sit beneath it.
 */
export const ANONYMOUS_BROWSABLE_EXACT_PATHS = new Set([
  "/cart",
  "/cart/checkout",
  standaloneProductsPaths.home,
  standaloneProductsPaths.browse,
  standaloneProductsPaths.cart,
  standaloneProductsPaths.checkout,
]);

// Opened by the native launch ticket's system browser, which has no session at all, not even a guest one.
const SESSIONLESS_EXTERNAL_RETURN_PATH = "/cart/checkout/beautifi-return";
const SESSIONLESS_EXTERNAL_RETURN_SURFACE_PARAM = "surface";
const SESSIONLESS_EXTERNAL_RETURN_SURFACE_VALUE = "external";

export const isSessionlessExternalReturnPath = (pathname: string, search: string): boolean =>
  pathname === SESSIONLESS_EXTERNAL_RETURN_PATH &&
  new URLSearchParams(search).get(SESSIONLESS_EXTERNAL_RETURN_SURFACE_PARAM) ===
    SESSIONLESS_EXTERNAL_RETURN_SURFACE_VALUE;

// Query keys carry the resolved `/api/v1` URL, not the template, so a stubbed path is matched
// for every id it was cached under.
const STUBBED_PATH_MATCHERS = ANONYMOUS_STUBBED_PATHS.map((path) => toTemplateMatcher(path));

const matchesPrefix = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const isAnonymousBrowsablePath = (pathname: string) =>
  ANONYMOUS_BROWSABLE_EXACT_PATHS.has(pathname) ||
  (!ANONYMOUS_DENIED_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix)) &&
    ANONYMOUS_BROWSABLE_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix)));

/**
 * Stub answers (empty rewards, empty cart, synthetic user) must never survive
 * a switch between anonymous and authenticated mode, in either direction.
 */
onAnonymousBrowsingChange(() => {
  queryClient.removeQueries({
    predicate: (query) => {
      const [url] = query.queryKey;
      return typeof url === "string" && STUBBED_PATH_MATCHERS.some((matcher) => matcher.test(url));
    },
  });
});

const ACCOUNT_CREATED_IN_CHECKOUT_KEY = "rmd.anonymousCheckout.accountCreatedInCheckout";

/**
 * Marks that an account was created from inside checkout, so the profile-completion redirect is
 * held back while the patient is still on a checkout page.
 *
 * Without this, verifying a phone number mid-checkout immediately bounces the patient to
 * /signin/gender — a question they have no reason to answer while a card is on screen, and the
 * most expensive possible moment to interrupt. Deferring is not skipping: the flag is only ever
 * consulted for /cart/checkout paths, so the first navigation anywhere else runs the ordinary
 * onboarding gate. Completing the order and abandoning it therefore both end in the same place.
 *
 * The location gate above it is deliberately NOT deferred: prices are location-scoped, and so is
 * the signup reward the patient is meant to be told about, so an account with no location has to
 * pick one before either can be right.
 */
export const markAccountCreatedInCheckout = () => {
  sessionStorage.setItem(ACCOUNT_CREATED_IN_CHECKOUT_KEY, "1");
};

/**
 * True when this session's account was created inside checkout. Such a patient never passed through
 * the cart page, which is where the tip selector normally lives, so checkout has to offer the tip
 * itself or they are silently charged without ever being asked.
 */
export const didCreateAccountInCheckout = () =>
  sessionStorage.getItem(ACCOUNT_CREATED_IN_CHECKOUT_KEY) === "1";

/** True only while a just-verified checkout is still on screen. */
export const isOnboardingDeferredForCheckout = (pathname: string) =>
  didCreateAccountInCheckout() &&
  (matchesPrefix(pathname, "/cart/checkout") ||
    matchesPrefix(pathname, standaloneProductsPaths.checkout));

export const setPostLoginReturnTo = (path: string) => {
  sessionStorage.setItem(POST_LOGIN_RETURN_TO_KEY, path);
};

export const consumePostLoginReturnTo = (): string | null => {
  const path = sessionStorage.getItem(POST_LOGIN_RETURN_TO_KEY);
  sessionStorage.removeItem(POST_LOGIN_RETURN_TO_KEY);
  return path;
};

/** Sends an anonymous visitor to sign-in, remembering where to come back to. */
export const goToSignIn = (navigate: NavigateFunction, returnTo?: string) => {
  if (returnTo) setPostLoginReturnTo(returnTo);
  navigate("/signin");
};

/** The route-loader form of `goToSignIn`, for returning out of a `LoaderFunction`. */
export const redirectToSignIn = (returnTo: string) => {
  setPostLoginReturnTo(returnTo);
  return redirect("/signin");
};
